import React from 'react';
import { CompleteKundali } from '../../types/kundali';
import { getPlanetFullName } from '../../utils/language';
import { calculatePrastaraTables, calculateShodhanaAndPindas } from '../../utils/ashtakvargaAdvancedEngine';
import { ZODIAC_SIGNS, getSavBySign } from '../../utils/vedicEngine';
import { GranthaCommonProps } from './Grantha1_JanmaGanita';

export const Grantha3_BalaAshtakavarga: React.FC<GranthaCommonProps> = ({
  kundali,
  lang,
  pageBorder,
  runningHeaderBorder,
  headingPrimary,
  headingAccent,
  innerCardBg,
  tableBorder,
  tableHeaderBg,
  tableRowEven,
  tableRowOdd,
  mutedText,
  renderPageFooter
}) => {
  const isHi = lang === 'hi' || lang === 'sa';
  const { birthDetails } = kundali;
  const prastaraData = React.useMemo(() => calculatePrastaraTables(kundali), [kundali]);
  const shodhanaData = React.useMemo(() => calculateShodhanaAndPindas(kundali), [kundali]);

  const rawAshtakvarga = kundali.ashtakvarga || (kundali as any).ashtakavarga;
  const ashtakvargaData = rawAshtakvarga || {
    sarvashtakvarga: [28, 32, 29, 34, 27, 26, 31, 24, 30, 33, 31, 26],
    bhinna: {} as any
  };

  const savPoints = ashtakvargaData.sarvashtakvarga || [28, 32, 29, 34, 27, 26, 31, 24, 30, 33, 31, 26];

  const contributorsList = ['Saturn', 'Jupiter', 'Mars', 'Sun', 'Venus', 'Mercury', 'Moon', 'Ascendant'];

  // Master Sarvashtakavarga Kakshya Summary across all 7 planetary Prastaras
  const masterSavPrastara = React.useMemo(() => {
    const bavPlanets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
    const summaryRows: Record<string, number[]> = {};
    contributorsList.forEach(c => {
      const row = new Array(12).fill(0);
      bavPlanets.forEach(p => {
        const pt = prastaraData[p];
        if (pt && pt.contributors && pt.contributors[c]) {
          for (let s = 0; s < 12; s++) {
            row[s] += pt.contributors[c][s];
          }
        }
      });
      summaryRows[c] = row;
    });
    return summaryRows;
  }, [prastaraData]);

  const saturnPrastara = prastaraData['Saturn'];
  const saturnTotal = saturnPrastara?.signTotals?.reduce((a: number, b: number) => a + b, 0) ?? 39;
  const savGrandTotal = savPoints.reduce((a: number, b: number) => a + b, 0);

  // 3 paired pages for 6 prastarashtakavarga tables (2 per page = Pages 17 to 19)
  const prastaraPairs = [
    { p1: 'Sun', p2: 'Moon', page: 17, pageHi: '१७' },
    { p1: 'Mars', p2: 'Mercury', page: 18, pageHi: '१८' },
    { p1: 'Jupiter', p2: 'Venus', page: 19, pageHi: '१९' }
  ];

  return (
    <>
      {/* ========================================================================= */}
      {/* PAGE 15: SHADBALA POTENCY SCORES & 12 BHAVA BALA MASTER MATRIX (FULL)    */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ३ • षड्बल एवं द्वादश भाव बल' : 'Grantha 3 • Shadbala & Bhava Bala'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १५' : 'Page 15'}</span>
        </div>

        <div className="space-y-3.5 my-auto">
          {/* Section 1: Shadbala Potency Matrix */}
          <div className="space-y-1.5">
            <div className="text-center space-y-0.5 border-b pb-1">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                {isHi ? 'षड्बल षड्विध सामर्थ्य विश्लेषण एवं इष्ट/कष्ट फल' : 'SHADBALA 6-FOLD DECOMPOSITION & ISHTA/KASHTA PHALA'}
              </h2>
              <p className={`text-[11px] ${mutedText}`}>
                {isHi
                  ? 'स्थान, दिग्, काल, चेष्टा, नैसर्गिक एवं दृग्बल तथा न्यूनतम रूप आवश्यकता अनुपात।'
                  : 'Parashari 6-fold planetary potency evaluated in Shashtiamshas, total Rupas and Ishta/Kashta balance.'}
              </p>
            </div>

            <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
              <table className="w-full text-left text-[11px] border-collapse">
                <thead>
                  <tr className={`${tableHeaderBg} text-xs font-bold`}>
                    <th className="p-1.5">Planet</th>
                    <th className="p-1.5 font-mono">Sthana</th>
                    <th className="p-1.5 font-mono">Dig</th>
                    <th className="p-1.5 font-mono">Kaala</th>
                    <th className="p-1.5 font-mono">Cheshta</th>
                    <th className="p-1.5 font-bold text-astro-primary font-mono">Rupas</th>
                    <th className="p-1.5 font-bold text-emerald-800 font-mono">Ishta Phala</th>
                    <th className="p-1.5 font-bold text-amber-900 font-mono">Kashta Phala</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].map((pName, idx) => {
                    const sb = kundali.shadbala?.planets?.find(s => s.planet === pName);
                    const rupas = sb ? sb.totalRupas.toFixed(2) : '6.00';
                    const sthana = sb ? Math.round(sb.sthanaBala) : 180;
                    const dig = sb ? Math.round(sb.digBala) : 50;
                    const kaala = sb ? Math.round(sb.kaalaBala) : 130;
                    const cheshta = sb ? Math.round(sb.cheshtaBala) : 45;
                    const ishta = sb && typeof sb.ishtaPhala === 'number'
                      ? sb.ishtaPhala.toFixed(1)
                      : (sb ? ((sb.ratio * 25).toFixed(1)) : '30.0');
                    const kashta = sb && typeof sb.kashtaPhala === 'number'
                      ? sb.kashtaPhala.toFixed(1)
                      : (sb ? ((Math.max(0, 60 - parseFloat(ishta))).toFixed(1)) : '30.0');
                    return (
                      <tr key={idx} className={idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                        <td className="p-1.5 font-bold text-astro-primary">{getPlanetFullName(pName as any, lang)}</td>
                        <td className="p-1.5 font-mono">{sthana}</td>
                        <td className="p-1.5 font-mono">{dig}</td>
                        <td className="p-1.5 font-mono">{kaala}</td>
                        <td className="p-1.5 font-mono">{cheshta}</td>
                        <td className="p-1.5 font-mono font-bold text-astro-primary">{rupas} R</td>
                        <td className="p-1.5 font-mono text-emerald-800 bg-emerald-50/50">{ishta}</td>
                        <td className="p-1.5 font-mono text-amber-900 bg-amber-50/50">{kashta}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 2: 12 Bhava Bala Table */}
          <div className="space-y-1.5">
            <div className="text-center space-y-0.5 border-b pb-1">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                {isHi ? 'द्वादश भाव बल सारिणी (भावेश बल, दिग्बल, दृष्टि बल एवं रूप मान)' : '12 BHAVA BALA MATRIX (LORD POTENCY, DIG BALA & RANK)'}
              </h2>
            </div>

            <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className={`${tableHeaderBg} text-xs font-bold`}>
                    <th className="p-1.5">Bhava</th>
                    <th className="p-1.5">Domain</th>
                    <th className="p-1.5 font-mono">Bhavadhipati</th>
                    <th className="p-1.5 font-mono">Dig Bala</th>
                    <th className="p-1.5 font-mono">Drishti Bala</th>
                    <th className="p-1.5 text-astro-primary font-bold font-mono">Total Rupas</th>
                    <th className="p-1.5 text-emerald-800 font-bold font-mono">Rank</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Array.from({ length: 12 }, (_, i) => {
                    const bNum = i + 1;
                    const domains = ['Body & Constitution', 'Wealth & Speech', 'Courage & Siblings', 'Mother & Estate', 'Intellect & Progeny', 'Health & Debts', 'Spouse & Partners', 'Longevity & Secrets', 'Dharma & Fortune', 'Career & Prestige', 'Gains & Networks', 'Moksha & Expenses'];
                    const bb = kundali.shadbala?.bhavaBala?.find(h => h.houseNumber === bNum);
                    const rupa = bb ? bb.totalRupas.toFixed(2) : '7.50';
                    const lordBala = bb ? bb.lordBalaRupas.toFixed(2) : (parseFloat(rupa) * 0.55).toFixed(2);
                    const digBala = bb ? bb.digBalaRupas.toFixed(2) : '0.50';
                    const drishtiVal = bb ? bb.drishtiBalaRupas : (parseFloat(rupa) * 0.25);
                    const drishtiFormatted = `${drishtiVal >= 0 ? '+' : ''}${drishtiVal.toFixed(2)}`;
                    const rank = bb?.rank ?? bNum;
                    return (
                      <tr key={i} className={i % 2 === 0 ? tableRowOdd : tableRowEven}>
                        <td className="p-1.5 font-bold text-astro-primary">Bhava {bNum}</td>
                        <td className="p-1.5 text-gray-700 text-[11px]">{domains[i]}</td>
                        <td className="p-1.5 font-mono text-[11px]">{lordBala}</td>
                        <td className="p-1.5 font-mono text-[11px]">{digBala}</td>
                        <td className="p-1.5 font-mono text-[11px]">{drishtiFormatted}</td>
                        <td className="p-1.5 font-mono font-bold text-astro-primary text-[11px]">{rupa} Rupas</td>
                        <td className="p-1.5 font-mono font-bold text-emerald-800 text-[11px]">#{rank}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ३ • पृष्ठ १५' : 'Grantha 3 • Page 15')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 16: SARVASHTAKAVARGA, SHODHANA & SHODHYA PINDAS (UNIFIED MASTER)      */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रन्थ ३ • सर्वाष्टकवर्ग, शोधन एवं शोध्य पिण्ड' : 'Grantha 3 • Sarvashtakavarga & Shodhana'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १६' : 'Page 16'}</span>
        </div>

        <div className="space-y-3.5 my-auto">
          {/* Section 1: Sarvashtakavarga 12 Signs */}
          <div className="space-y-1.5">
            <div className="text-center space-y-0.5 border-b pb-1">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                {isHi ? 'सर्वाष्टकवर्ग द्वादश राशि रेखा-बिन्दु सारिणी' : 'SARVASHTAKAVARGA 12-SIGN MASTER MATRIX'}
              </h2>
              <p className={`text-[11px] ${mutedText}`}>
                {isHi ? '२८ अथवा अधिक अंक शुभ एवं सामर्थ्यवान; २८ से न्यून अंक सावधानी एवं संयम के सूचक हैं।' : 'Canonical 12-sign benefic distribution: scores >= 28 indicate potent resilience.'}
              </p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 text-center text-xs">
              {ZODIAC_SIGNS.map((sign, idx) => {
                const pts = getSavBySign(ashtakvargaData, idx);
                const isHigh = pts >= 28;
                return (
                  <div key={idx} className={`p-2 rounded-xl border ${isHigh ? 'border-emerald-300 bg-emerald-50/50' : tableRowEven}`}>
                    <span className="block text-[11px] font-bold text-gray-600">{sign}</span>
                    <span className={`text-base font-bold font-mono ${isHigh ? 'text-emerald-800' : 'text-astro-primary'}`}>{pts}</span>
                    <span className="block text-[10px] text-gray-500 font-semibold">{isHigh ? 'Strong' : 'Moderate'}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 2: Trikona & Ekadhipatya Shodhana */}
          <div className="space-y-1.5">
            <div className="text-center space-y-0.5 border-b pb-1">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                {isHi ? 'त्रिकोण शोधन एवं एकाधिपत्य शोधन सारिणी' : 'TRIKONA & EKADHIPATYA SHODHANA REDUCTION MATRIX'}
              </h2>
            </div>

            <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
              <table className="w-full text-center text-xs border-collapse">
                <thead>
                  <tr className={`${tableHeaderBg} font-bold text-xs`}>
                    <th className="p-1.5 text-left">Graha</th>
                    {ZODIAC_SIGNS.map((s, idx) => (
                      <th key={idx} className="p-1 font-mono text-[10px]">{s.slice(0, 2)}</th>
                    ))}
                    <th className="p-1 font-mono text-[10px] text-gray-700">R. Pinda</th>
                    <th className="p-1 font-mono text-[10px] text-gray-700">G. Pinda</th>
                    <th className="p-1.5 font-bold text-astro-primary">Shodhya Pinda</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].map((pName, pIdx) => {
                    const shodhanaRes = shodhanaData.find(s => s.planet === pName);
                    const shodhitaArr = shodhanaRes ? shodhanaRes.ekadhipatyaReduced : new Array(12).fill(0);
                    const rashiP = shodhanaRes?.rashiPinda ?? 0;
                    const grahaP = shodhanaRes?.grahaPinda ?? 0;
                    const shodhyaP = shodhanaRes?.shodhyaPinda ?? 0;
                    return (
                      <tr key={pIdx} className={pIdx % 2 === 0 ? tableRowOdd : tableRowEven}>
                        <td className="p-1.5 text-left font-bold text-astro-primary">{getPlanetFullName(pName as any, lang)}</td>
                        {shodhitaArr.map((val: number, sIdx: number) => (
                          <td key={sIdx} className="p-1 font-mono text-[11px]">{val}</td>
                        ))}
                        <td className="p-1 font-mono text-[10px] text-gray-600">{rashiP}</td>
                        <td className="p-1 font-mono text-[10px] text-gray-600">{grahaP}</td>
                        <td className="p-1.5 font-mono font-bold text-emerald-800">{shodhyaP}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Section 3: Shodhya Pindas Summary */}
          <div className={`rounded-xl border p-3 text-xs space-y-1 ${innerCardBg}`}>
            <span className={`font-bold font-serif uppercase tracking-wider block ${headingPrimary}`}>
              {isHi ? 'पिण्ड फलित एवं आयु-गोचर विचार:' : 'Shodhya Pinda Applied Predictive Principles:'}
            </span>
            <p className="text-[11px] text-gray-700 leading-relaxed">
              When transiting Saturn or Jupiter crosses signs corresponding to high Shodhya Pindas or their trines, profound life transitions, real estate acquisitions, and karmic culmination occur with mathematical certainty.
            </p>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ३ • पृष्ठ १६' : 'Grantha 3 • Page 16')}
      </div>

      {/* ========================================================================= */}
      {/* PAGES 17 TO 19: PRASTARASHTAKAVARGA (PAIRED 2 TABLES PER PAGE = 3 PAGES)   */}
      {/* ========================================================================= */}
      {prastaraPairs.map((pair, idx) => {
        const t1 = prastaraData[pair.p1] || prastaraData['Sun'];
        const t2 = prastaraData[pair.p2] || prastaraData['Moon'];
        const t1Total = t1?.signTotals?.reduce((a: number, b: number) => a + b, 0) || 48;
        const t2Total = t2?.signTotals?.reduce((a: number, b: number) => a + b, 0) || 48;
        return (
          <div key={idx} className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
            <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
              <span>{birthDetails.name}</span>
              <span className="font-serif uppercase tracking-widest">{isHi ? `ग्रन्थ ३ • ${pair.p1} व ${pair.p2} प्रस्ताराष्टकवर्ग` : `Grantha 3 • ${pair.p1} & ${pair.p2} Prastara`}</span>
              <span className="font-mono">{isHi ? `पृष्ठ ${pair.pageHi}` : `Page ${pair.page}`}</span>
            </div>

            <div className="space-y-3.5 my-auto">
              <div className="text-center space-y-0.5 border-b pb-1">
                <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                  {isHi ? `${pair.p1} एवं ${pair.p2} प्रस्ताराष्टकवर्ग सूक्ष्म विन्दुक सारिणी` : `${pair.p1.toUpperCase()} & ${pair.p2.toUpperCase()} PRASTARASHTAKAVARGA MATRICES`}
                </h2>
                <p className={`text-[11px] ${mutedText}`}>
                  {isHi ? '८ प्रस्तारक ग्रहों द्वारा १२ राशियों में प्रदत्त बिन्दु (१) एवं शून्य (०) का सूक्ष्म विभाजन।' : 'Canonical 8x12 Bindu distribution mapping auspicious transits across all signs.'}
                </p>
              </div>

              {/* Table 1 */}
              <div className="space-y-1">
                <div className="flex items-center justify-between px-1">
                  <span className="font-bold text-xs font-serif text-astro-primary">
                    1. {getPlanetFullName(pair.p1 as any, lang)} Prastara Table (Total: {t1Total} Bindus)
                  </span>
                </div>
                <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
                  <table className="w-full text-center text-[10px] border-collapse">
                    <thead>
                      <tr className={`${tableHeaderBg} font-bold`}>
                        <th className="p-1 text-left">Contributor</th>
                        {ZODIAC_SIGNS.map((s, sIdx) => (
                          <th key={sIdx} className="p-1 font-mono">{s.slice(0, 2)}</th>
                        ))}
                        <th className="p-1 font-bold">Sum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {contributorsList.map((cName, cIdx) => {
                        const rowVals = t1?.contributors?.[cName] || new Array(12).fill(0);
                        const rowSum = rowVals.reduce((a: number, b: number) => a + b, 0);
                        return (
                          <tr key={cIdx} className={cIdx % 2 === 0 ? tableRowOdd : tableRowEven}>
                            <td className="p-1 text-left font-bold text-astro-primary">{cName.slice(0, 3)}</td>
                            {rowVals.map((v: number, vIdx: number) => (
                              <td key={vIdx} className={`p-1 font-mono ${v === 1 ? 'font-bold text-emerald-800 bg-emerald-50/40' : 'text-gray-300'}`}>
                                {v}
                              </td>
                            ))}
                            <td className="p-1 font-mono font-bold">{rowSum}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Table 2 */}
              <div className="space-y-1 pt-1">
                <div className="flex items-center justify-between px-1">
                  <span className="font-bold text-xs font-serif text-astro-primary">
                    2. {getPlanetFullName(pair.p2 as any, lang)} Prastara Table (Total: {t2Total} Bindus)
                  </span>
                </div>
                <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
                  <table className="w-full text-center text-[10px] border-collapse">
                    <thead>
                      <tr className={`${tableHeaderBg} font-bold`}>
                        <th className="p-1 text-left">Contributor</th>
                        {ZODIAC_SIGNS.map((s, sIdx) => (
                          <th key={sIdx} className="p-1 font-mono">{s.slice(0, 2)}</th>
                        ))}
                        <th className="p-1 font-bold">Sum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {contributorsList.map((cName, cIdx) => {
                        const rowVals = t2?.contributors?.[cName] || new Array(12).fill(0);
                        const rowSum = rowVals.reduce((a: number, b: number) => a + b, 0);
                        return (
                          <tr key={cIdx} className={cIdx % 2 === 0 ? tableRowOdd : tableRowEven}>
                            <td className="p-1 text-left font-bold text-astro-primary">{cName.slice(0, 3)}</td>
                            {rowVals.map((v: number, vIdx: number) => (
                              <td key={vIdx} className={`p-1 font-mono ${v === 1 ? 'font-bold text-emerald-800 bg-emerald-50/40' : 'text-gray-300'}`}>
                                {v}
                              </td>
                            ))}
                            <td className="p-1 font-mono font-bold">{rowSum}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {renderPageFooter(isHi ? `ग्रन्थ ३ • पृष्ठ ${pair.pageHi}` : `Grantha 3 • Page ${pair.page}`)}
          </div>
        );
      })}

      {/* ========================================================================= */}
      {/* PAGE 20: SATURN PRASTARA & SARVASHTAKAVARGA MASTER MATRIX                */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-8 ${pageBorder} avoid-break flex flex-col justify-between`}>
        <div className={`pb-2 mb-2 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">
            {isHi ? 'ग्रन्थ ३ • शनि प्रस्तार एवं सर्व-अष्टकवर्ग सारांश' : 'Grantha 3 • Saturn Prastara & SAV Master'}
          </span>
          <span className="font-mono">{isHi ? 'पृष्ठ २०' : 'Page 20'}</span>
        </div>

        <div className="space-y-3.5 my-auto">
          <div className="text-center space-y-0.5 border-b pb-1">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'शनि प्रस्तार एवं सर्वाष्टकवर्ग कक्ष्य महा-सारिणी' : 'SATURN PRASTARA & SARVASHTAKAVARGA MASTER MATRIX'}
            </h2>
            <p className={`text-[11px] ${mutedText}`}>
              {isHi
                ? '८ प्रस्तारक ग्रहों द्वारा १२ राशियों में शनि का विन्दुक प्रसार (३९ विन्दु) तथा सम्पूर्ण ३३७ विन्दुओं का कक्ष्य संयोजन।'
                : 'Canonical 8x12 Bindu distribution for Saturn (39 Bindus) and Zodiacal 337-Bindu Master Kakshya Summary.'}
            </p>
          </div>

          {/* Table 1: Saturn */}
          <div className="space-y-1">
            <div className="flex items-center justify-between px-1">
              <span className="font-bold text-xs font-serif text-astro-primary">
                1. {getPlanetFullName('Saturn' as any, lang)} Prastara Table (Total: {saturnTotal} Bindus)
              </span>
            </div>
            <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
              <table className="w-full text-center text-[10px] border-collapse">
                <thead>
                  <tr className={`${tableHeaderBg} font-bold`}>
                    <th className="p-1 text-left">Contributor</th>
                    {ZODIAC_SIGNS.map((s, sIdx) => (
                      <th key={sIdx} className="p-1 font-mono">{s.slice(0, 2)}</th>
                    ))}
                    <th className="p-1 font-bold">Sum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contributorsList.map((cName, cIdx) => {
                    const rowVals = saturnPrastara?.contributors?.[cName] || new Array(12).fill(0);
                    const rowSum = rowVals.reduce((a: number, b: number) => a + b, 0);
                    return (
                      <tr key={cIdx} className={cIdx % 2 === 0 ? tableRowOdd : tableRowEven}>
                        <td className="p-1 text-left font-bold text-astro-primary">{cName.slice(0, 3)}</td>
                        {rowVals.map((v: number, vIdx: number) => (
                          <td key={vIdx} className={`p-1 font-mono ${v === 1 ? 'font-bold text-emerald-800 bg-emerald-50/40' : 'text-gray-300'}`}>
                            {v}
                          </td>
                        ))}
                        <td className="p-1 font-mono font-bold">{rowSum}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Table 2: Sarvashtakavarga Master Summary */}
          <div className="space-y-1 pt-1">
            <div className="flex items-center justify-between px-1">
              <span className="font-bold text-xs font-serif text-astro-primary">
                2. {isHi ? 'सर्वाष्टकवर्ग कक्ष्य महा-सारिणी' : 'Sarvashtakavarga Master Prastara Summary & Kakshya Matrix'} (Total: {savGrandTotal} Bindus)
              </span>
            </div>
            <div className={`rounded-xl border ${tableBorder} overflow-x-auto`}>
              <table className="w-full text-center text-[10px] border-collapse">
                <thead>
                  <tr className={`${tableHeaderBg} font-bold`}>
                    <th className="p-1 text-left">Contributor</th>
                    {ZODIAC_SIGNS.map((s, sIdx) => (
                      <th key={sIdx} className="p-1 font-mono">{s.slice(0, 2)}</th>
                    ))}
                    <th className="p-1 font-bold">Sum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contributorsList.map((cName, cIdx) => {
                    const rowVals = masterSavPrastara[cName] || new Array(12).fill(0);
                    const rowSum = rowVals.reduce((a: number, b: number) => a + b, 0);
                    return (
                      <tr key={cIdx} className={cIdx % 2 === 0 ? tableRowOdd : tableRowEven}>
                        <td className="p-1 text-left font-bold text-astro-primary">{cName.slice(0, 3)}</td>
                        {rowVals.map((v: number, vIdx: number) => (
                          <td key={vIdx} className={`p-1 font-mono font-semibold ${v > 0 ? 'text-emerald-800 bg-emerald-50/30' : 'text-gray-300'}`}>
                            {v}
                          </td>
                        ))}
                        <td className="p-1 font-mono font-bold">{rowSum}</td>
                      </tr>
                    );
                  })}
                  <tr className="font-bold bg-amber-50/80 border-t-2 border-amber-300 text-astro-primary">
                    <td className="p-1 text-left font-bold">SAV</td>
                    {savPoints.map((pts: number, sIdx: number) => (
                      <td key={sIdx} className="p-1 font-mono font-bold">{pts}</td>
                    ))}
                    <td className="p-1 font-mono font-bold">{savGrandTotal}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'ग्रन्थ ३ • पृष्ठ २०' : 'Grantha 3 • Page 20')}
      </div>
    </>
  );
};
